import React, { useEffect, useState } from "react";
import {useNavigate, useParams} from "react-router-dom";
import {
    Box,
    Typography,
    Button,
    Grid,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Paper, CircularProgress, CardActions
} from "@mui/material";
import {
    getServerSettings,
    updateServerSettings,
    getServerStats,
    downloadBackup,
    restoreBackup,
    createBackup,
    listBackups,
    indexServer, wipeServer, jobServer
} from "../http/ServerAPI";
import {LOG_ROUTE, SERVER_PLUGINS_ROUTE, SERVER_SETTINGS, SERVERS_ROUTE} from "../utils/consts";
import BreadCrumbs from "../components/Breadcrumbs";
import Container from "@mui/material/Container";
import ServerSettingsForm from "../components/ServerSettingsForm";
import ServerBackups from "../components/ServerBackups";
import ServerStatsChart from "../components/ServerStatsChart";
import {ListAlt, PlayArrow, RocketLaunch, Settings, Source, Stop, TipsAndUpdates} from "@mui/icons-material";
import {getApiErrorMessage} from "../http/apiError";

const ServerSettings = () => {
    const navigate = useNavigate();
    const { server_id } = useParams();
    const [settings, setSettings] = useState(null);
    const [server, setServer] = useState([]);
    const [stats, setStats] = useState(null);
    const [dialog, setDialog] = useState({ open: false, action: null });
    const [downloading, setDownloading] = useState(false);
    const [backups, setBackups] = useState([]);
    const [confirm, setConfirm] = useState({ open: false, action: null, text: "", serverId: null });
    const [loadingButton, setLoadingButton] = useState(null);

    const hrefs = [
        { href: SERVERS_ROUTE, name: "Список серверов" },
    ];

    useEffect(() => {
        loadSettings();
        loadStats();
        loadBackups();
        loadServer();
    }, [server_id]);

    const handleConfirm = (text, action, serverId) => {
        setConfirm({ open: true, action, text, serverId });
    };

    const handleConfirmClose = () => {
        setConfirm({ open: false, action: null, text: "", serverId: null });
    };

    const handleConfirmedAction = async () => {
        if (!confirm.action) return;
        setLoadingButton(confirm.action);
        handleConfirmClose();

        try {
            if (confirm.action === "wipe") {
                await wipeServer(confirm.serverId);
                await loadServer(); // ✅ можно обновить после вайпа сразу
            } else {
                await jobServer(confirm.serverId, confirm.action);
                await loadServer(); // ✅ обновляем только если НЕ старт
            }
        } catch (e) {
            console.error(e);
        } finally {
            setLoadingButton(null);
        }
    };

    const loadSettings = async () => {
        const data = await getServerSettings(server_id);
        setSettings(data);
    };

    const loadServer = async () => {
        const data = await indexServer(server_id);
        setServer(data);
        console.log(data);
    }

    const loadBackups = async () => {
        try {
            const data = await listBackups(server_id);
            const parsed = data.backups.map(b => {
                const match = b.name.match(/\d{4}-\d{2}-\d{2}_\d{2}-\d{2}-\d{2}/);
                let dateTime = "Неизвестно";
                if (match) {
                    const [date, time] = match[0].split('_');
                    const formatted = `${date}T${time.replace(/-/g, ':')}`; // Превращаем в формат ISO
                    const d = new Date(formatted);
                    if (!isNaN(d)) {
                        dateTime = d.toLocaleString("ru-RU");
                    }
                }
                return {
                    name: b.name,
                    sizeMb: (b.size / (1024 * 1024)).toFixed(2),
                    dateTime,
                };
            });
            setBackups(parsed);
        } catch (e) {
            console.error(getApiErrorMessage(e, 'Ошибка при получении бэкапов'));
        }
    };

    const loadStats = async () => {
        const data = await getServerStats(server_id);
        setStats(data.map(item => ({
            cpu: item.cpu_usage / 8, // делим на 8 ядер
            memory: item.memory_rss,
            time: new Date(item.CreatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            fullTime: new Date(item.CreatedAt).toLocaleString('ru-RU', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })
        })));
    };

    const handleChange = (field, value) => {
        setSettings(prev => ({ ...prev, [field]: value }));
    };

    const openDialog = (action) => {
        setDialog({ open: true, action });
    };

    const closeDialog = () => {
        setDialog({ open: false, action: null });
    };

    const confirmDialog = async () => {
        closeDialog();
        if (dialog.action === "save") {
            await updateServerSettings(server_id, settings);
            await loadSettings();
        }
        if (dialog.action === "backup") {
            await createBackup(server_id);
        }
        if (dialog.action === "restore") {
            await restoreBackup(server_id);
        }
        if (dialog.action === "download") {
            setDownloading(true);
            try {
                await downloadBackup(server_id, setDownloading); // передаём setDownloading
            } catch (e) {
                alert(getApiErrorMessage(e, 'Ошибка при скачивании'));
            } finally {
                setDownloading(false);
            }
        }
    };

    const isRunning = server.status_id === 3;
    const isStopped = server.status_id === 4 || server.status_id === 8;
    const isStarting = server.status_id === 5;


    if (!settings) return <Typography>Загрузка настроек...</Typography>;

    return (
        <Container maxWidth="xl">
            <Box p={3}>
                <Box sx={{ width: '100%', marginBottom: 3 }}>
                    <BreadCrumbs hrefs={hrefs} name='Настройки сервера' />
                </Box>
                <Typography variant="h4" mb={3}>Настройки сервера</Typography>
                <Grid container spacing={2}>
                    {/* Основные настройки */}
                    <Grid item xs={12} md={6}>
                        <ServerSettingsForm settings={settings} handleChange={handleChange} openDialog={openDialog} />
                    </Grid>

                    {/* Бэкапы и графики */}
                    <Grid item xs={12} md={6}>
                        <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
                            <Typography variant="h6" gutterBottom>Управление сервером</Typography>
                            <CardActions sx={{ flexWrap: 'wrap', justifyContent: 'center' }}>
                                {isStopped && (
                                    <Button size="small" color="success" startIcon={loadingButton === "start" ? <CircularProgress size={20} /> : <PlayArrow />}
                                            onClick={() => handleConfirm("Запустить сервер?", "start", server.server_id)} disabled={!!loadingButton}>
                                        Старт
                                    </Button>
                                )}
                                {isRunning && (
                                    <Button size="small" color="error" startIcon={loadingButton === "stop" ? <CircularProgress size={20} /> : <Stop />}
                                            onClick={() => handleConfirm("Остановить сервер?", "stop", server.server_id)} disabled={!!loadingButton}>
                                        Стоп
                                    </Button>
                                )}
                                <Button size="small" color="warning" startIcon={loadingButton === "update" ? <CircularProgress size={20} /> : <TipsAndUpdates />}
                                        onClick={() => handleConfirm("Обновить сервер?", "update", server.server_id)} disabled={!!loadingButton}>
                                    Обновить
                                </Button>
                                <Button size="small" color="info" startIcon={loadingButton === "wipe" ? <CircularProgress size={20} /> : <RocketLaunch />}
                                        onClick={() => handleConfirm("Выполнить вайп сервера?", "wipe", server.server_id)} disabled={!!loadingButton}>
                                    Вайп
                                </Button>
                                <Button size="small" color="primary" startIcon={<Source />}
                                        onClick={() => navigate(SERVER_PLUGINS_ROUTE + '/' + server.server_id)}>
                                    Плагины
                                </Button>
                                <Button size="small" color="secondary" startIcon={<ListAlt />}
                                        onClick={() => navigate(LOG_ROUTE + '/' + server.server_id)}>
                                    Логи
                                </Button>
                            </CardActions>
                        </Paper>

                        <ServerBackups backups={backups} downloading={downloading} openDialog={openDialog} />

                        <ServerStatsChart stats={stats} />
                    </Grid>
                </Grid>
                {/* Подтверждение */}
                <Dialog open={dialog.open} onClose={closeDialog}>
                    <DialogTitle>Подтвердите действие</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Вы уверены, что хотите {dialog.action === "save" ? "сохранить изменения" : dialog.action === "backup" ? "создать бэкап" : dialog.action === "restore" ? "восстановить сервер" : "скачать бэкап"}?
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={closeDialog}>Отмена</Button>
                        <Button onClick={confirmDialog} autoFocus>Подтвердить</Button>
                    </DialogActions>
                </Dialog>

                <Dialog open={confirm.open} onClose={handleConfirmClose}>
                    <DialogTitle>Подтверждение действия</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            {confirm.text}
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleConfirmClose}>Отмена</Button>
                        <Button onClick={handleConfirmedAction} color="primary" autoFocus>Подтвердить</Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </Container>
    );
};

export default ServerSettings;
